import { useEffect } from 'react'
import {
  useAppDispatch,
  useAppSelector,
  useIsSharer,
  useSelectAuth,
  useSelectNeedsEmailConsent,
  useSelectOrigin,
  useSelectRunningContext,
  useSelectMobileDeviceInfo,
} from '.'
import { App, setPage, selectIsNavigatingDuringCollab } from '../app/root'
import { useLogoutMutation } from '../app/appApiQueries'
import { CollabFile, selectCollabFile } from '../app/collabFile'
import { selectFile } from '../app/file'

// logic flow for setting the numerous different app pages
export const useSetPage = (app: App) => {
  const dispatch = useAppDispatch()
  const runningContext = useSelectRunningContext()
  const origin = useSelectOrigin()
  const isCollaborate = runningContext === 'inCollaborate'
  const { isMobileDevice, isLatestMobileClient, isAndroid } =
    useSelectMobileDeviceInfo()
  const auth = useSelectAuth(app)
  const collabFile = useAppSelector(selectCollabFile) as CollabFile
  const needsEmailConsent = useSelectNeedsEmailConsent(app)

  const file = useAppSelector(selectFile)
  const isNavigatingDuringCollab = useAppSelector(
    selectIsNavigatingDuringCollab
  )
  const [, { logoutSuccess }] = useLogoutMutation({
    selectFromResult: (result) => ({
      logoutSuccess: result?.data?.success,
    }),
    fixedCacheKey: 'logout',
  })

  const isSharer = useIsSharer(app)!

  const isContactsSupported = auth?.extra?.isContactsSupported

  // ============================= Start V3 =============================
  // Uncomment this Effect to go to back to native collaboration
  /**
 useEffect(() => {
    function collaborationNavigation() {
      /* OLD CODE: MS NATIVE 
      if (!collabFile?.fileUrl) return //  do nothing and keep waiting until the collabFile appears
      // if we are the google app
      if (app === 'Google') {
        // and navigating during collaboration
        if (isNavigatingDuringCollab) {
          dispatch(setPage('home'))
        } else {
          dispatch(setPage('joinCollaboration'))
        }
        // otherwise we are the MS app
        // if we are navigating during collaboration
      } else if (isNavigatingDuringCollab) {
        // and we've selected a file to view
        if (file?.id) {
          dispatch(setPage('microsoftFileView'))
        } else {
          dispatch(setPage('home'))
        }
        // otherwise we are viewing the file we are collaborating on
      } else dispatch(setPage('microsoftFileViewCollab'))
    }

    if (auth?.authStatus !== undefined) {
      // and we are manually logged out
      if (logoutSuccess) dispatch(setPage('disconnected'))
      // and we are not logged in
      // else if (auth?.authStatus === 0 || !isContactsSupported) // TODO: add back once People.Read.All scope is added back
      else if (auth?.authStatus === 0)
        dispatch(setPage('connectToDrive'))
      // and we are logged in
      else if (auth?.authStatus === 1) {
        // if we are in collaboration
        if (isCollaborate) {
          if (isSharer === undefined) {
            if (origin === 'Meeting Chat' && app === 'Google') {
              dispatch(setPage('home'))
            }
            return
          }
          // do nothing until we have sharerDetails
          if (isSharer) {
            collaborationNavigation()
          } else {
            // we've not checked if we are a contact yet
            if (needsEmailConsent !== undefined) {
              // if we do not have access or are not a contact of the sender
              // and we do not have file details
              if (needsEmailConsent) {
                dispatch(setPage('shareEmail'))
              } else {
                collaborationNavigation()
              }
              // if we've not checked if we need to request email access
            } else {
              dispatch(setPage('grantingDocAccess'))
            }
          }
          // if we aren't collaborating
        } else if (file?.id && app === 'Microsoft') {
          dispatch(setPage('microsoftFileView'))
        } else {
          dispatch(setPage('home'))
        }
      }
    }
  }, [
    auth?.authStatus,
    isContactsSupported,
    runningContext,
    dispatch,
    isSharer,
    collabFile?.fileUrl,
    isCollaborate,
    needsEmailConsent,
    logoutSuccess,
    file?.id,
    app,
    isNavigatingDuringCollab,
    origin,
  ])
   */
  // ============================= End V3 =============================

  // ============================= Start V3 =============================
  // Delete this this Effect to go to back to native collaboration
  useEffect(() => {
    function collaborationNavigation() {
      if (!collabFile?.fileUrl) return //  do nothing and keep waiting until the collabFile appears
      // if we are the google or MS app
      // and navigating during collaboration
      if (isNavigatingDuringCollab) {
        dispatch(setPage('home'))
      } else {
        dispatch(setPage('joinCollaboration'))
      }
    }

    /**
     * If the client is a Mobile Device, which is a non-Android & non-supported client,
     * than show the mobileClientUpgrade screen.
     * For Chat Running Contexts, it just works fine for old clients as well.
     */
    if (
      isMobileDevice &&
      !isAndroid &&
      !isLatestMobileClient &&
      runningContext !== 'inChat'
    ) {
      dispatch(setPage('mobileClientUpgrade'))
      return
    }

    if (auth?.authStatus !== undefined) {
      // and we are manually logged out
      if (logoutSuccess) dispatch(setPage('disconnected'))
      // and we are not logged in
      else if (auth?.authStatus === 0 || !isContactsSupported)
        dispatch(setPage('connectToDrive'))
      // and we are logged in
      else if (auth?.authStatus === 1) {
        // if we are in collaboration
        if (isCollaborate) {
          if (isSharer === undefined) {
            if (origin === 'Meeting Chat') {
              dispatch(setPage('home'))
            }
            return
          }
          // do nothing until we have sharerDetails
          if (isSharer) {
            collaborationNavigation()
          } else {
            // we've not checked if we are a contact yet
            if (needsEmailConsent !== undefined) {
              // if we do not have access or are not a contact of the sender
              // and we do not have file details
              if (needsEmailConsent) {
                dispatch(setPage('shareEmail'))
              } else {
                collaborationNavigation()
              }
              // if we've not checked if we need to request email access
            } else {
              dispatch(setPage('grantingDocAccess'))
            }
          }
          // if we aren't collaborating
        } else {
          dispatch(setPage('home'))
        }
      }
    }
  }, [
    auth?.authStatus,
    isContactsSupported,
    runningContext,
    dispatch,
    isSharer,
    collabFile?.fileUrl,
    isCollaborate,
    needsEmailConsent,
    logoutSuccess,
    file?.id,
    app,
    isNavigatingDuringCollab,
    origin,
    isLatestMobileClient,
    isAndroid,
    isMobileDevice,
  ])
  // ============================= End V3 =============================
}
